import { useEffect, useMemo, useState } from 'react';
import type { OrderRooftopFacts } from '@hopdrive/feature-order-index';

import type { OrderType } from '@features/products/catalog';
import type { CountWindow, ProductCounts, ProductCountsResult } from '@features/products/counts/productCounts';
import { useGuestRidesCounts } from '@features/guestRides/counts/useGuestRidesCounts';
import { useMobileServiceCounts } from '@features/mobileService/counts/useMobileServiceCounts';
import { usePartsDeliveryCounts } from '@features/partsDelivery/counts/usePartsDeliveryCounts';
import { useValetCounts } from '@features/orderIndex/products/valet/counts/useValetCounts';

import { msUntilNextDay, todayWindow } from '../utils/dayWindow';
import { HOME_POLL_OPTIONS } from './homePolling';

export interface HomeProductCountsInput {
  /** Empty skips every query. */
  customerIds: number[];
  /** The facts the Valet adapter derives each row with. */
  rooftops: OrderRooftopFacts[];
  /** The order types the customer owns. Nothing is fetched for the others. */
  orderTypes: OrderType[];
  /** The per-customer tallies run only when the by-rooftop breakdown shows. */
  breakdown: boolean;
  /** Skip every query (e.g. the breakdown pass for a viewer who does not span rooftops). */
  skip?: boolean;
}

export interface HomeProductCountsState {
  byProduct: Partial<Record<OrderType, ProductCounts>>;
  /** The first load only: a running query without data yet; later polls update in place. */
  loading: boolean;
  errors: Partial<Record<OrderType, Error>>;
  refetch: () => void;
  /** Today's window, the one every module was asked for. */
  window: CountWindow;
}

/** From the viewer's local day. */
const todayCountWindow = (): CountWindow => {
  const { start, end } = todayWindow();
  return { startIso: start, endIso: end };
};

/**
 * Each product's figures come from its own shared counts module, with payer inclusion off.
 * A failed product leaves the others' figures rendering. A timer set for the next local
 * midnight re-arms itself, so a Home left open overnight polls the new day.
 */
export function useHomeProductCounts({ customerIds, rooftops, orderTypes, breakdown, skip = false }: HomeProductCountsInput): HomeProductCountsState {
  const [day, setDay] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setDay(n => n + 1), msUntilNextDay());
    return () => clearTimeout(timer);
  }, [day]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `day` is the trigger (the midnight timer bumps it), not a value the memo reads
  const window = useMemo(() => todayCountWindow(), [day]);

  const owns = (orderType: OrderType) => orderTypes.includes(orderType);
  const common = { customerIds, window, includePayer: false, breakdown, pollOptions: HOME_POLL_OPTIONS };

  const valet = useValetCounts({ ...common, rooftops, skip: skip || !owns('moves') });
  const mobileService = useMobileServiceCounts({ ...common, skip: skip || !owns('mobile-service') });
  const partsDelivery = usePartsDeliveryCounts({ ...common, skip: skip || !owns('parts-delivery') });
  const guestRides = useGuestRidesCounts({ ...common, skip: skip || !owns('guest-rides') });

  // Valet's module now comes from the order-index package, which restates the counts shapes
  // structurally (its `product` is the order-type union, a subset of the catalog's `ProductKey`).
  // The four results meet at the portal's `ProductCountsResult`, the wider of the two.
  const results: Array<[OrderType, ProductCountsResult]> = [
    ['moves', valet],
    ['mobile-service', mobileService],
    ['parts-delivery', partsDelivery],
    ['guest-rides', guestRides],
  ];
  const owned = results.filter(([orderType]) => owns(orderType));

  const byProduct = useMemo(() => {
    const out: Partial<Record<OrderType, ProductCounts>> = {};
    for (const [orderType, result] of owned) if (result.counts) out[orderType] = result.counts;
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the four results' counts are the inputs
  }, [valet.counts, mobileService.counts, partsDelivery.counts, guestRides.counts, orderTypes]);

  const errors = useMemo(() => {
    const out: Partial<Record<OrderType, Error>> = {};
    for (const [orderType, result] of owned) if (result.error) out[orderType] = result.error;
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the four results' errors are the inputs
  }, [valet.error, mobileService.error, partsDelivery.error, guestRides.error, orderTypes]);

  return {
    byProduct,
    loading: owned.some(([, result]) => result.loading),
    errors,
    refetch: () => owned.forEach(([, result]) => result.refetch()),
    window,
  };
}
